import { OUTPUT_FACTORY_IDS } from './outputPerformanceConfig.js';

const normalizeName = value =>
  String(value || '')
    .trim()
    .toLowerCase();

const slugifyStationPath = value =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const flatten = items => items.reduce((acc, item) => acc.concat(item), []);

const uniqueBy = (records, getKey) => {
  const seen = new Set();

  return records.filter(record => {
    const key = getKey(record);
    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const getPersonName = record =>
  record.userName ||
  record.operatorName ||
  record.employeeName ||
  record.userFullName ||
  (record.userId ? `User ${record.userId}` : 'Unassigned');

const getRecordUnitCount = record =>
  Number.isFinite(Number(record.outputCount)) ? Number(record.outputCount) : 1;

const getBoxesAndItems = records =>
  records.reduce(
    (acc, record) => {
      const isBox = record.isBox ?? record.IsBox;
      if (typeof isBox !== 'boolean') {
        return acc;
      }

      acc.hasBoxData = true;
      const unit = getRecordUnitCount(record);
      if (isBox) {
        acc.boxesCount += unit;
      } else {
        acc.itemsCount += unit;
      }

      return acc;
    },
    { hasBoxData: false, itemsCount: 0, boxesCount: 0 }
  );

const getRecordCount = (records, metricType) => {
  if (!records.length) {
    return 0;
  }

  if (metricType === 'sashes') {
    const sashRecords = records.filter(record => Number(record.sashNo) > 0);

    if (sashRecords.length) {
      return uniqueBy(
        sashRecords,
        record => `${record.headerId}-${record.frameId}-${record.sashNo}`
      ).length;
    }
  }

  return records.reduce((total, record) => total + getRecordUnitCount(record), 0);
};

const groupRecords = (records, groupBy, metricType) => {
  const grouped = records.reduce((acc, record) => {
    const key = groupBy(record);
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(record);
    return acc;
  }, {});

  return Object.keys(grouped)
    .map(name => ({
      name,
      value: getRecordCount(grouped[name], metricType)
    }))
    .sort((a, b) => b.value - a.value);
};

const groupRecordsWithItemTypeDetail = (records, groupBy, metricType, itemTypeRecords = []) => {
  const grouped = records.reduce((acc, record) => {
    const key = groupBy(record);
    if (!acc[key]) acc[key] = [];
    acc[key].push(record);
    return acc;
  }, {});

  const itemTypeGrouped = itemTypeRecords.reduce((acc, record) => {
    const key = groupBy(record);
    if (!acc[key]) acc[key] = [];
    acc[key].push(record);
    return acc;
  }, {});

  return Object.keys(grouped)
    .map(name => {
      const groupedRecs = grouped[name];
      const hasItemType = groupedRecs.some(r => r.itemType || r.ItemType || r.itemTypeName || r.ItemTypeName);

      let itemTypeDetail = null;
      if (hasItemType) {
        itemTypeDetail = getItemTypeBreakdown(groupedRecs);
      } else if (itemTypeGrouped[name]?.length) {
        itemTypeDetail = getItemTypeBreakdown(itemTypeGrouped[name]);
      }

      return {
        name,
        value: getRecordCount(groupedRecs, metricType),
        itemTypeDetail
      };
    })
    .sort((a, b) => b.value - a.value);
};

const getItemType = record =>
  record.itemType ||
  record.ItemType ||
  record.itemTypeName ||
  record.ItemTypeName ||
  'Other';

const getItemTypeBreakdown = records =>
  groupRecords(records, getItemType, 'items');

const groupBoxRecords = (records, groupBy) => {
  const grouped = records.reduce((acc, record) => {
    const key = groupBy(record);
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(record);
    return acc;
  }, {});

  return Object.keys(grouped)
    .map(name => {
      const summary = getBoxesAndItems(grouped[name]);
      const value = summary.itemsCount + summary.boxesCount;

      return {
        name,
        value,
        hasBoxData: summary.hasBoxData,
        itemsCount: summary.itemsCount,
        boxesCount: summary.boxesCount
      };
    })
    .sort((a, b) => b.value - a.value);
};

const flattenWeldedCillSource = records =>
  (Array.isArray(records) ? records : [])
    .filter(record => record)
    .map(record => {
      const stationName = record.stationName ?? record.StationName;
      const stationPath = slugifyStationPath(stationName);
      const shift = record.shift ?? record.Shift;
      const userId = record.userId ?? record.UserId;
      const boxId = record.boxId ?? record.BoxId;
      const totalFacets = Number(record.totalFacets ?? record.TotalFacets) || 0;

      return {
        ...record,
        stationName,
        stationPath,
        shift,
        userId,
        boxId,
        totalFacets,
        normalizedStationName: normalizeName(stationName),
        normalizedStationPath: normalizeName(slugifyStationPath(stationPath)),
        sourceType: 'weldedCill',
        source: 'api',
        isMock: false
      };
    })
    .filter(record => record.stationName || record.stationPath);

const getWeldedCillStats = records => {
  const uniqueBoxIds = new Set(records.map(r => r.boxId).filter(id => id != null));
  const totalFacets = records.reduce((sum, r) => sum + r.totalFacets, 0);
  return { boxesCreated: uniqueBoxIds.size, totalFacets };
};

const groupWeldedCillRecords = (records, groupBy) => {
  const grouped = records.reduce((acc, record) => {
    const key = groupBy(record);
    if (!acc[key]) acc[key] = [];
    acc[key].push(record);
    return acc;
  }, {});

  return Object.keys(grouped)
    .map(name => {
      const stats = getWeldedCillStats(grouped[name]);
      return {
        name,
        value: stats.boxesCreated,
        hasWeldedCillData: true,
        weldedCillBoxesCreated: stats.boxesCreated,
        weldedCillTotalFacets: stats.totalFacets
      };
    })
    .sort((a, b) => b.value - a.value);
};

const flattenSource = (records, sourceType) =>
  (Array.isArray(records) ? records : [])
    .filter(record => record)
    .map(record => {
      const stationName = record.stationName ?? record.StationName;
      const stationPath =
        record.stationPath ??
        record.StationPath ??
        slugifyStationPath(stationName);
      const shift = record.shift ?? record.Shift;
      const outputCount = record.outputCount ?? record.OutputCount;
      const userId = record.userId ?? record.UserId;

      return {
        ...record,
        stationName,
        stationPath,
        shift,
        outputCount,
        userId,

        // Normalized fields used for station matching.
        normalizedStationName: normalizeName(stationName),
        normalizedStationPath: normalizeName(slugifyStationPath(stationPath)),

        sourceType,
        source: 'api',
        isMock: false
      };
    })
    .filter(record => record.stationName || record.stationPath);

const getFactoryConfig = (factoryId, factories = []) =>
  factories.find(factory => factory.id === factoryId) ||
  factories.find(
    factory => factory.id === OUTPUT_FACTORY_IDS.EMPLAS_1
  );

const getMatchingRecords = (records, stationConfig) => {
  const stationName = normalizeName(stationConfig.name);
  const stationPath = normalizeName(slugifyStationPath(stationConfig.stationPath));
  const inferredStationPath = normalizeName(slugifyStationPath(stationConfig.name));

  return records.filter(record =>
    stationPath
      ? record.normalizedStationPath === stationPath
      : record.normalizedStationPath === inferredStationPath ||
        record.normalizedStationName === stationName
  );
};

const collectSource = records => {
  if (!records.length) {
    return 'none';
  }

  const hasApi = records.some(record => record.source === 'api');
  const hasMock = records.some(record => record.source === 'mock');

  if (hasApi && hasMock) {
    return 'mixed';
  }

  if (hasApi) {
    return 'api';
  }

  return 'mock';
};

const buildStationView = ({
  records,
  numberOfPiecesRecords,
  numberOfFrameBoxesRecords,
  weldedCillRecords,
  compositeDoorGearingRecords,
  readlyToLoadRecords,
  stationConfig,
  metricType
}) => {
  const weldedCillStationRecords = getMatchingRecords(weldedCillRecords, stationConfig);
  const hasWeldedCillData = metricType === 'weldedCill' || weldedCillStationRecords.length > 0;

  const readlyToLoadStationRecords = getMatchingRecords(readlyToLoadRecords, stationConfig);

  let stationRecords;
  if (hasWeldedCillData) {
    stationRecords = [];
  } else if (metricType === 'piecesCut') {
    const piecesMatched = getMatchingRecords(numberOfPiecesRecords, stationConfig);
    stationRecords = piecesMatched.length
      ? piecesMatched
      : getMatchingRecords(records, stationConfig);
  } else if (metricType === 'frameBoxes') {
    const frameBoxMatched = getMatchingRecords(numberOfFrameBoxesRecords, stationConfig);
    stationRecords = frameBoxMatched.length
      ? frameBoxMatched
      : getMatchingRecords(records, stationConfig);
  } else if (metricType === 'compositeDoorGearing') {
    stationRecords = compositeDoorGearingRecords;
  } else if (readlyToLoadStationRecords.length > 0) {
    stationRecords = readlyToLoadStationRecords;
  } else {
    stationRecords = getMatchingRecords(records, stationConfig);
  }

  const weldedCillStats = hasWeldedCillData
    ? getWeldedCillStats(weldedCillStationRecords)
    : { boxesCreated: 0, totalFacets: 0 };

  const boxSummary = getBoxesAndItems(stationRecords);
  const frameBoxMatchedForStation = getMatchingRecords(numberOfFrameBoxesRecords, stationConfig);
  const stationHasItemType = stationRecords.some(r => r.itemType || r.ItemType || r.itemTypeName || r.ItemTypeName);
  const itemTypeSource = stationHasItemType ? stationRecords : frameBoxMatchedForStation;
  const allRecordsForSource = hasWeldedCillData ? weldedCillStationRecords : stationRecords;

  return {
    name: stationConfig.name,
    stationPath: stationConfig.stationPath,
    displayType: stationConfig.displayType,
    metricType,
    isResolved: stationConfig.isResolved !== false || (metricType === 'compositeDoorGearing' && compositeDoorGearingRecords.length > 0),
    resolutionNote: stationConfig.resolutionNote || null,
    source: collectSource(allRecordsForSource),
    records: allRecordsForSource,
    total: hasWeldedCillData ? weldedCillStats.boxesCreated : getRecordCount(stationRecords, metricType),
    hasBoxData: boxSummary.hasBoxData,
    itemsCount: boxSummary.itemsCount,
    boxesCount: boxSummary.boxesCount,
    hasWeldedCillData,
    weldedCillBoxesCreated: weldedCillStats.boxesCreated,
    weldedCillTotalFacets: weldedCillStats.totalFacets,
    itemTypeBreakdown: itemTypeSource.length ? getItemTypeBreakdown(itemTypeSource) : null,
    shiftBreakdown: hasWeldedCillData
      ? groupWeldedCillRecords(weldedCillStationRecords, record => record.shift || 'N/A')
      : boxSummary.hasBoxData
        ? groupBoxRecords(stationRecords, record => record.shift || 'N/A')
        : groupRecordsWithItemTypeDetail(stationRecords, record => record.shift || 'N/A', metricType, frameBoxMatchedForStation),
    peopleBreakdown: hasWeldedCillData
      ? groupWeldedCillRecords(weldedCillStationRecords, getPersonName)
      : boxSummary.hasBoxData
        ? groupBoxRecords(stationRecords, getPersonName)
        : groupRecordsWithItemTypeDetail(stationRecords, getPersonName, metricType, frameBoxMatchedForStation)
  };
};

const mergeBreakdowns = breakdowns =>
  Object.values(
    flatten(breakdowns).reduce((acc, item) => {
      if (!acc[item.name]) {
        acc[item.name] = { name: item.name, value: 0 };
      }
      acc[item.name].value += item.value;
      return acc;
    }, {})
  ).sort((a, b) => b.value - a.value);

const buildGroupView = ({ records, numberOfPiecesRecords, numberOfFrameBoxesRecords, weldedCillRecords, compositeDoorGearingRecords, readlyToLoadRecords, groupConfig, factoryId, areaName }) => {
  const metricType = groupConfig.metricType || 'items';
  const stations = groupConfig.stations.map(stationConfig =>
    buildStationView({
      records,
      numberOfPiecesRecords,
      numberOfFrameBoxesRecords,
      weldedCillRecords,
      compositeDoorGearingRecords,
      readlyToLoadRecords,
      stationConfig,
      factoryId,
      areaName,
      groupName: groupConfig.name,
      metricType
    })
  );
  const stationRecords = flatten(stations.map(stationView => stationView.records));

  return {
    name: groupConfig.name,
    displayType: groupConfig.displayType,
    metricType,
    stations,
    source: collectSource(stationRecords),
    total: stations.reduce((total, stationView) => total + stationView.total, 0),
    shiftBreakdown: mergeBreakdowns(
      stations.map(stationView => stationView.shiftBreakdown)
    ),
    peopleBreakdown: mergeBreakdowns(
      stations.map(stationView => stationView.peopleBreakdown)
    )
  };
};

const buildAreaView = ({ records, numberOfPiecesRecords, numberOfFrameBoxesRecords, weldedCillRecords, compositeDoorGearingRecords, readlyToLoadRecords, areaConfig, factoryId }) => {
  const groups = areaConfig.groups.map(groupConfig =>
    buildGroupView({
      records,
      numberOfPiecesRecords,
      numberOfFrameBoxesRecords,
      weldedCillRecords,
      compositeDoorGearingRecords,
      readlyToLoadRecords,
      groupConfig,
      factoryId,
      areaName: areaConfig.name
    })
  );
  const areaRecords = flatten(
    flatten(groups.map(group => group.stations)).map(station => station.records)
  );

  return {
    name: areaConfig.name,
    groups,
    source: collectSource(areaRecords),
    total: groups.reduce((total, group) => total + group.total, 0),
    shiftBreakdown: mergeBreakdowns(groups.map(group => group.shiftBreakdown)),
    peopleBreakdown: mergeBreakdowns(groups.map(group => group.peopleBreakdown))
  };
};

export const buildOutputPerformanceView = ({
  factoryId,
  factories = [],
  productionItems,
  ancillaryItems,
  hardwareItems,
  numberOfPiecesItems,
  numberOfFrameBoxesItems,
  weldedCillItems,
  compositeDoorGearingItems,
  readlyToLoadItems
}) => {
  const factoryConfig = getFactoryConfig(factoryId, factories);
  if (!factoryConfig) {
    return {
      id: factoryId,
      name: 'Output Performance',
      source: 'none',
      total: 0,
      areas: []
    };
  }

  const records = [
    ...flattenSource(productionItems, 'production'),
    ...flattenSource(ancillaryItems, 'ancillary'),
    ...flattenSource(hardwareItems, 'hardware'),
  ];
  const readlyToLoadRecords = flattenSource(readlyToLoadItems, 'readlyToLoad');
  const numberOfPiecesRecords = flattenSource(numberOfPiecesItems, 'numberOfPieces');
  const numberOfFrameBoxesRecords = flattenSource(numberOfFrameBoxesItems, 'numberOfFrameBoxes');
  const weldedCillRecords = flattenWeldedCillSource(weldedCillItems);
  const compositeDoorGearingRecords = flattenSource(compositeDoorGearingItems, 'compositeDoorGearing');
  const areas = factoryConfig.areas.map(areaConfig =>
    buildAreaView({
      records,
      numberOfPiecesRecords,
      numberOfFrameBoxesRecords,
      weldedCillRecords,
      compositeDoorGearingRecords,
      readlyToLoadRecords,
      areaConfig,
      factoryId: factoryConfig.id,
    })
  );

  return {
    id: factoryConfig.id,
    name: factoryConfig.name,
    source: collectSource(
      flatten(
        flatten(
          flatten(areas.map(area => area.groups)).map(group => group.stations)
        ).map(station => station.records)
      )
    ),
    total: areas.reduce((total, area) => total + area.total, 0),
    areas
  };
};
